import { type JSX, useId, useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from './ui/popover.tsx'
import { useTheme } from './theme-provider.tsx'
import { useTimeZone } from './time-zone-provider.tsx'
import { Button } from '../design-system/button.tsx'
import { Icon, type IconName } from '../design-system/icon.tsx'
import { Select } from '../design-system/select.tsx'
import { cn } from '../lib/cn.ts'
import type { ThemePreference } from '../lib/theme.ts'
import { timeZoneLabel, timeZoneOptions } from '../lib/time-zone.ts'
import { useSession } from '../session.tsx'

/** Each theme choice's label and glyph. A Record, so a new preference with no label fails type-check. */
const THEME_CHOICES: Record<
  ThemePreference,
  { label: string; icon: IconName }
> = {
  light: { label: 'Light', icon: 'sun' },
  dark: { label: 'Dark', icon: 'moon' },
  system: { label: 'System', icon: 'monitor' }
}

/** The order the menu offers the choices in. */
const THEME_ORDER: readonly ThemePreference[] = ['light', 'dark', 'system']

export interface AccountMenuProps {
  /** The prefix of every hook inside: `-menu`, `-user`, `-sign-out` and so on. */
  'data-testid': string
  /** Closes the mobile sheet before a sign-out runs. */
  onSelect: () => void
}

/**
 * Up to two letters for a name: one per word of an address before its `@`.
 *
 * A word that starts with a digit gives none, so the uid fallback `uid-1`
 * reads `U` rather than `U1`.
 *
 * @param name - An address, or the uid when the token carries no email.
 * @returns The letters, or `?` when the name holds none.
 */
function initialsOf(name: string): string {
  const letters = (name.split('@')[0] ?? '')
    .split(/[._\-\s]+/)
    .filter(word => /^\p{L}/u.test(word))
    .map(word => (Array.from(word)[0] ?? '').toUpperCase())
    .slice(0, 2)
    .join('')

  return letters || '?'
}

/**
 * A small uppercase heading over one group of the open menu's controls.
 *
 * @param props - Heading props.
 * @param props.id - Lets the group name itself by this heading.
 * @param props.children - The heading's words.
 * @returns The heading.
 */
function SectionHeading({
  id,
  children
}: {
  id: string
  children: string
}): JSX.Element {
  return (
    <p
      id={id}
      className="text-muted-foreground text-xs font-medium tracking-wide uppercase"
    >
      {children}
    </p>
  )
}

/**
 * The time zone select, with each zone's abbreviation and offset as of now.
 *
 * The popover mounts this on every open, so the lazy initializer builds the
 * list then: not at load, where 400 formatters would slow the first paint,
 * and fresh after a daylight saving change.
 *
 * @param props - Field props.
 * @param props.data-testid - Test hook for the select.
 * @param props.timeZone - The zone the reader holds.
 * @param props.onChange - Holds a newly chosen zone.
 * @returns The labelled select.
 */
function TimeZoneField({
  'data-testid': testId,
  timeZone,
  onChange
}: {
  'data-testid': string
  timeZone: string
  onChange: (timeZone: string) => void
}): JSX.Element {
  const [options] = useState(timeZoneOptions)
  const listed = options.some(option => option.value === timeZone)

  return (
    <Select
      data-testid={testId}
      label="Time zone"
      hint="Every date and time in a report is shown in this zone."
      options={
        listed
          ? options
          : [{ value: timeZone, label: timeZoneLabel(timeZone) }, ...options]
      }
      value={timeZone}
      onChange={event => onChange(event.target.value)}
    />
  )
}

/**
 * AccountMenu — who is signed in, how figures read, and the way out.
 *
 * One rail row stands for all of it, so the foot stays one item however many
 * apps the page list gains. The row still prints the zone and the theme,
 * because the zone changes what every report says, and a reader must see it
 * without opening anything. Each takes its own line and glyph, and the theme
 * says "theme", because a bare "Dark" after a zone read as part of the zone.
 *
 * The open menu keeps the two apart too: the zone sits under Date & time,
 * the theme under Appearance, so neither reads as a setting of the other.
 *
 * The theme offers Light, Dark and System. The pressed choice is what the
 * reader holds, not what is painted: System on a light OS paints light, and
 * Light must still read unpressed, or a choice looks like a default.
 *
 * The popover is a light surface, so its controls are the page's own `Select`
 * and `Button` rather than rail rows.
 *
 * @param props - Menu props.
 * @param props.data-testid - Prefix for every test hook inside.
 * @param props.onSelect - Closes the mobile sheet before a sign-out.
 * @returns The rendered menu.
 */
export function AccountMenu({
  'data-testid': testId,
  onSelect
}: AccountMenuProps): JSX.Element {
  const { userName, onSignOut } = useSession()
  const { timeZone, setTimeZone } = useTimeZone()
  const { preference, setPreference } = useTheme()
  const themeLabelId = useId()
  const dateTimeHeadingId = useId()
  const appearanceHeadingId = useId()
  const zone = timeZoneLabel(timeZone)
  const theme = `${THEME_CHOICES[preference].label} theme`
  const summary = `${zone} · ${theme}`
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-menu`}
          // The row's text is three nodes, and a name built from them joins
          // them with no separator. The label keeps the visible words, so a
          // voice command that reads them still finds the control.
          aria-label={`${userName}, ${zone} time zone, ${theme}, account and display settings`}
          className={cn(
            'text-sidebar-foreground/80 flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm',
            'hover:bg-sidebar-accent/40 focus-visible:ring-sidebar-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none',
            'data-[state=open]:bg-sidebar-accent/60'
          )}
        >
          <span
            data-testid={`${testId}-initials`}
            aria-hidden="true"
            className="bg-sidebar-accent text-sidebar-accent-foreground flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
          >
            {initialsOf(userName)}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span
              data-testid={`${testId}-user`}
              className="text-sidebar-foreground truncate"
            >
              {userName}
            </span>
            <span
              data-testid={`${testId}-summary`}
              title={summary}
              className="text-sidebar-foreground/60 flex min-w-0 flex-col gap-0.5 text-xs"
            >
              <span
                data-testid={`${testId}-summary-zone`}
                className="flex min-w-0 items-center gap-1"
              >
                <Icon name="clock" className="size-3 shrink-0" />
                <span className="truncate">{zone}</span>
              </span>
              <span
                data-testid={`${testId}-summary-theme`}
                className="flex min-w-0 items-center gap-1"
              >
                <Icon
                  name={THEME_CHOICES[preference].icon}
                  className="size-3 shrink-0"
                />
                <span className="truncate">{theme}</span>
              </span>
            </span>
          </span>
          <Icon name="chevrons-up-down" className="size-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid={`${testId}-menu-content`}
        aria-label="Account and display settings"
        side="top"
        align="start"
        className="w-72 p-0"
      >
        <div className="px-4 py-3">
          <p className="text-muted-foreground text-xs">Signed in as</p>
          <p
            data-testid={`${testId}-menu-user`}
            className="text-sm font-medium break-all"
          >
            {userName}
          </p>
        </div>
        <section
          aria-labelledby={dateTimeHeadingId}
          className="grid gap-3 border-t px-4 py-3"
        >
          <SectionHeading id={dateTimeHeadingId}>
            Date &amp; time
          </SectionHeading>
          <TimeZoneField
            data-testid={`${testId}-time-zone`}
            timeZone={timeZone}
            onChange={setTimeZone}
          />
        </section>
        <section
          aria-labelledby={appearanceHeadingId}
          className="grid gap-3 border-t px-4 py-3"
        >
          <SectionHeading id={appearanceHeadingId}>Appearance</SectionHeading>
          <div
            role="group"
            aria-labelledby={themeLabelId}
            className="grid gap-2"
          >
            <span
              id={themeLabelId}
              className="text-sm leading-none font-medium"
            >
              Theme
            </span>
            <div className="grid grid-cols-3 gap-2">
              {THEME_ORDER.map(choice => (
                <Button
                  key={choice}
                  data-testid={`${testId}-theme-${choice}`}
                  variant={preference === choice ? 'primary' : 'outline'}
                  aria-pressed={preference === choice}
                  className="h-auto flex-col gap-1 py-2"
                  onClick={() => setPreference(choice)}
                >
                  <Icon name={THEME_CHOICES[choice].icon} className="size-4" />
                  {THEME_CHOICES[choice].label}
                </Button>
              ))}
            </div>
          </div>
        </section>
        <div className="border-t p-1">
          <Button
            data-testid={`${testId}-sign-out`}
            variant="quiet"
            className="w-full justify-start"
            onClick={() => {
              setOpen(false)
              onSelect()
              onSignOut()
            }}
          >
            <Icon name="log-out" className="size-4" />
            Sign out
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
